import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { SplitLines } from "@/components/site/Motion";
import { scrollTo } from "@/components/site/Navbar";

export const Hero = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.setAttribute("muted", "");
    v.playsInline = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    // Retry once the video has data + on first user interaction (iOS low-power mode)
    v.addEventListener("loadeddata", tryPlay, { once: true });
    const onInteract = () => { tryPlay(); };
    document.addEventListener("touchstart", onInteract, { once: true });
    document.addEventListener("click", onInteract, { once: true });
    return () => {
      v.removeEventListener("loadeddata", tryPlay);
      document.removeEventListener("touchstart", onInteract);
      document.removeEventListener("click", onInteract);
    };
  }, []);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[56vh] min-h-[380px] sm:h-screen sm:min-h-[640px] w-full overflow-hidden grain"
    >
      <motion.div style={{ scale: videoScale, y: videoY }} className="absolute inset-0">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          src="/media/hero-reef.mp4"
          poster="/media/hero-poster.jpg"
          preload="auto"
          autoPlay
          loop
          muted
          playsInline
          {...{ "webkit-playsinline": "true", "x5-playsinline": "true" }}
          data-testid="hero-video"
        />
      </motion.div>

      <div className="absolute inset-0 bg-[#090b1c]/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0d1026] via-[#0d1026]/5 to-[#090b1c]/40" />

      <div className="absolute inset-0 z-10 flex items-center justify-center px-6 pt-28 sm:pt-0">
        <div className="w-full max-w-6xl text-center">
          <p
            className="font-body font-light tracking-[0.34em] uppercase text-sm lg:text-base text-[var(--foam)]/80 mb-6"
            data-testid="hero-kicker"
          >
            Arizona's Trusted
          </p>

          <h1 className="font-title text-[var(--foam)] leading-[1.02] [text-shadow:0_2px_30px_rgba(9,11,28,0.6)]">
            <SplitLines
              lines={["EXPERTS FOR SALTWATER, REEF", "& FRESHWATER AQUARIUMS"]}
              delay={0.3}
              className="text-lg sm:text-4xl lg:text-[3.4rem] font-extrabold uppercase"
            />
          </h1>

          <div className="flex flex-wrap items-center justify-center gap-5 mt-11">
            <button
              onClick={() => scrollTo("#contact")}
              data-testid="hero-cta-primary"
              className="inline-flex items-center rounded-full px-10 py-4 font-body font-light text-[var(--foam)] bg-gradient-to-r from-[#5b5be0] to-[#8b5cf6] shadow-lg shadow-[#5b5be0]/30 hover:from-[#6d6df0] hover:to-[#9d6dff] hover:-translate-y-0.5 transition-all duration-300"
            >
              Let's Talk
            </button>
            <button
              onClick={() => navigate("/water-delivery")}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center rounded-full px-10 py-4 font-body font-light text-[var(--foam)] bg-gradient-to-r from-[#5b5be0] to-[#8b5cf6] shadow-lg shadow-[#5b5be0]/30 hover:from-[#6d6df0] hover:to-[#9d6dff] hover:-translate-y-0.5 transition-all duration-300"
            >
              Delivery
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
